import React, { useState } from 'react';
import {
    Upload, Download, Search, Filter, FileText, Image, File, Eye,
    Edit, Trash2, Clock, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Button } from './Button';

export const DocumentCenter = () => {
    const [documents] = useState([
        {
            id: '1',
            name: 'Invoice_ABC_001.pdf',
            type: 'PDF',
            category: 'Sales Invoice',
            client: 'ABC Industries',
            uploadDate: '2024-01-15',
            size: '2.4 MB',
            status: 'Processed',
            version: 1,
            extractedData: { amount: 25000, gst: 4500 },
            signatureStatus: 'Signed'
        },
        {
            id: '2',
            name: 'Bank_Statement_Jan.xlsx',
            type: 'Excel',
            category: 'Bank Statement',
            client: 'XYZ Corp',
            uploadDate: '2024-01-14',
            size: '1.8 MB',
            status: 'Processing',
            version: 1,
            signatureStatus: 'Not Required'
        }
    ]);

    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        'Sales Invoices', 'Purchase Bills', 'Bank Statements', 'GST Returns',
        'Contracts', 'Compliance Documents', 'Financial Statements'
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Processed': return 'bg-green-100 text-green-800';
            case 'Processing': return 'bg-blue-100 text-blue-800';
            case 'Error': return 'bg-red-100 text-red-800';
            case 'Pending Review': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getFileIcon = (type) => {
        switch (type.toLowerCase()) {
            case 'pdf': return <FileText className="h-5 w-5 text-red-500" />;
            case 'excel': return <File className="h-5 w-5 text-green-500" />;
            case 'image': return <Image className="h-5 w-5 text-blue-500" />;
            default: return <File className="h-5 w-5 text-gray-500" />;
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Document Center</h1>
                    <div className="flex space-x-3">
                        <Button label="Bulk Upload" />
                        <Button label="Upload Document" />

                    </div>
                </div>

                <div className="flex items-center space-x-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search documents by name, client, or content..."
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>
                    <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
                        <Filter className="h-4 w-4" />
                        <span>Advanced Filters</span>
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 border rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Documents</p>
                            <p className="text-2xl font-bold text-gray-900">1,247</p>
                        </div>
                        <FileText className="h-8 w-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white p-6 border rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">OCR Processed</p>
                            <p className="text-2xl font-bold text-green-600">1,156</p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-500" />
                    </div>
                </div>
                <div className="bg-white p-6 border rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Processing Queue</p>
                            <p className="text-2xl font-bold text-blue-600">23</p>
                        </div>
                        <Clock className="h-8 w-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white p-6 border rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Errors</p>
                            <p className="text-2xl font-bold text-red-600">8</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500" />
                    </div>
                </div>
            </div>

            {/* Document List */}
            <div className="bg-white border rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Documents</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Document', 'Client', 'Category', 'Status', 'Extracted Data', 'Actions'].map(header => (
                                    <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {documents.map(doc => (
                                <tr key={doc.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {getFileIcon(doc.type)}
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900">{doc.name}</div>
                                                <div className="text-sm text-gray-500">{doc.size} • v{doc.version}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{doc.client}</td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{doc.category}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                                            {doc.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {doc.extractedData ? (
                                            <div>
                                                <div>Amount: ₹{doc.extractedData.amount.toLocaleString()}</div>
                                                <div>GST: ₹{doc.extractedData.gst.toLocaleString()}</div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Processing...</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button className="text-blue-600 hover:text-blue-900"><Eye className="h-4 w-4" /></button>
                                            <button className="text-gray-600 hover:text-gray-900"><Download className="h-4 w-4" /></button>
                                            <button className="text-gray-600 hover:text-gray-900"><Edit className="h-4 w-4" /></button>
                                            <button className="text-red-600 hover:text-red-900"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Signature Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Digital Signature Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-medium text-green-900">Signed Documents</h4>
                        <p className="text-2xl font-bold text-green-600">892</p>
                        <p className="text-sm text-green-700">Valid signatures</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-medium text-yellow-900">Pending Signatures</h4>
                        <p className="text-2xl font-bold text-yellow-600">15</p>
                        <p className="text-sm text-yellow-700">Awaiting approval</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-900">Certificate Validity</h4>
                        <p className="text-2xl font-bold text-blue-600">245</p>
                        <p className="text-sm text-blue-700">Days remaining</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
